import React, { useState, useEffect, useRef } from "react";
import {
    LottieLoadingIndicator,
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalSection,
    StandardModalSelect,
    StandardModalState,
    Toast,
    UserSelectionMetricCopy,
    UserSelectionMetricDescription,
    UserSelectionMetricSummary,
    UserSelectionMetricTitle,
    UserSelectionMetricValue,
    UserViewerListPreview
} from "src/components";
import { AUDIENCE } from "../Advertising";
import axios from "axios";
import { API_URL } from "src/scenes/App";
import pluralize from "pluralize";
import { customNames, capitalizeString } from "src/utils/helpers";

const getAdvertisingViewers = (token, ad) => {
    const controller = new AbortController();
    const isMountedRef = useRef(true);
    const [advertisingViewers, setAdvertisingViewers] = useState([]);
    const [isFetchingViewers, setIsFetchingViewers] = useState(false);

    useEffect(() => {
        const fetch = async () => {
            setIsFetchingViewers(true);
            try {
                const config = {
                    signal: controller.signal,
                    headers: {
                        Authorization: "Bearer " + token
                    }
                };

                const response = await axios.get(`${API_URL}/advertising/${ad.uuid}/visitors`, config);
                
                if (isMountedRef.current) {
                    setAdvertisingViewers(response.data.visitors);
                }
            } catch {
                if (isMountedRef.current) {
                    Toast.error("Unable to fetch advertisement. Please try again later or contact support if the issue persists.");
                }
            } finally {
                if (isMountedRef.current) {
                    setIsFetchingViewers(false);
                }
            }
        };

        fetch();

        return () => {
            controller.abort();
            isMountedRef.current = false;
        };

    }, [token]);

    return { advertisingViewers, isFetchingViewers };
};

const AdvertisingViewsModal = ({ adData, showAdViewsModal, handleCloseAdViewsModal, user }) => {
    const { advertisingViewers, isFetchingViewers } = getAdvertisingViewers(user.token, adData);
    
    const userTypeDefault = () => {
        if (adData.displayToEmployees) {
            return AUDIENCE.EMPLOYEE;
        } else if (adData.displayToClients) {
            return AUDIENCE.CLIENT;
        } else {
            return AUDIENCE.EMPLOYEE; //default to employees
        }
    };

    const filterVisitorsByUserType = (userType) => {
        return userType === AUDIENCE.EMPLOYEE ? 
            advertisingViewers.employees : 
            advertisingViewers.clients;
    };

    const [userType, setUserType] = useState(userTypeDefault());
    const [viewersList, setViewersList] = useState(null);

    const handleUserTypeChange = (e) => {
        const value = e.target.value;
        setUserType(value);
    };

    useEffect(() => {
        const filteredList = filterVisitorsByUserType(userType);
        setViewersList(filteredList);
    }, [isFetchingViewers]);
    
    useEffect(() => {
        const filteredList = filterVisitorsByUserType(userType);
        setViewersList(filteredList);
    }, [userType]);

    const getTotalLinkClicks = (viewersList) => {
        if (viewersList.length === 0)
            return 0;

        return viewersList.map(employeeDetails => employeeDetails.numberOfViews).reduce((total, numberOfViews) => total + numberOfViews, 0);
    };
    
    const viewCount = viewersList ? getTotalLinkClicks(viewersList) : 0;
    const viewsCountFormatted = `${parseInt(viewCount).toLocaleString("en-us")} ${viewCount > 1 || viewCount === 0 ? "Views" : "View"}`;

    return (
        <StandardModal
            open={showAdViewsModal}
            onClose={handleCloseAdViewsModal}
            title="Ad Impressions"
            widthMode="wide"
            headerAside={(
                <div style={{ width: "13rem" }}>
                    <StandardModalSelect value={userType} onChange={handleUserTypeChange}>
                        <option value={AUDIENCE.EMPLOYEE}>{capitalizeString(pluralize(customNames.employees))}</option>
                        <option value={AUDIENCE.CLIENT}>{capitalizeString(pluralize(customNames.clients))}</option>
                    </StandardModalSelect>
                </div>
            )}
            footer={(
                <StandardModalActionGroup $pushRight>
                    <StandardModalActionButton type="button" onClick={handleCloseAdViewsModal}>
                        Close
                    </StandardModalActionButton>
                </StandardModalActionGroup>
            )}
        >
            <StandardModalSection>
                <UserSelectionMetricSummary>
                    <UserSelectionMetricCopy>
                        <UserSelectionMetricTitle>View Ad Impressions</UserSelectionMetricTitle>
                        <UserSelectionMetricDescription>
                            See who has viewed your ad. Filter detailed impressions from employees or clients to understand campaign reach.
                        </UserSelectionMetricDescription>
                    </UserSelectionMetricCopy>
                    {!isFetchingViewers && <UserSelectionMetricValue>{viewsCountFormatted}</UserSelectionMetricValue>}
                </UserSelectionMetricSummary>
            </StandardModalSection>

            <StandardModalSection>
                {isFetchingViewers ? (
                    <StandardModalState minHeight="20rem">
                        <LottieLoadingIndicator />
                    </StandardModalState>
                ) : (
                    <UserViewerListPreview
                        viewersList={viewersList}
                        isReaderTypeEmployee={userType === AUDIENCE.EMPLOYEE}
                        clientSecondaryHeader="Client Type"
                        emptyMessage="No user views found"
                    />
                )}
            </StandardModalSection>
        </StandardModal>
    );
};

export default AdvertisingViewsModal;
